/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import {
  PEOPLE_LIMIT,
  PEOPLE_QUERY_MAX,
  isAddress,
  isPaste,
  looksFinished,
  peopleIn,
  typedPeople,
  withPeople,
  withoutPerson,
  type KnownPerson,
} from '../lib/people';
import { Button, Input } from '../ui';

/** How long typing pauses before the search runs. */
const SEARCH_DELAY_MS = 250;
/** The most suggestions shown at once. */
const SHOWN = 8;

type Search =
  | { status: 'idle' }
  | { status: 'searching' }
  | {
      status: 'failed';
      /** The HTTP status the service answered with, or null when it was not reached. */
      code: number | null;
    }
  | { status: 'done'; query: string; people: KnownPerson[]; total: number; complete: boolean };

/** What a failed search says. A sign-in that expired, or a console role that may not search, is not an outage. */
function failedText(code: number | null): string {
  if (code === 401) return 'Your sign-in has expired. Sign in again to search people.';
  if (code === 403) return 'Only IT admins can search people or change these lists.';
  return 'The people Gyro knows could not be read right now. Type a full e-mail address instead.';
}

/** The note for what Enter or Add left in the box: words that are not addresses, and addresses that look unfinished. */
function leftText(rejected: string[], unfinished: string[]): string {
  const parts: string[] = [];
  if (rejected.length > 0) parts.push(`Not added: ${rejected.join(', ')}.`);
  if (unfinished.length > 0) {
    parts.push(`${unfinished.join(', ')} ${unfinished.length === 1 ? 'looks' : 'look'} unfinished. Type the whole address, or pick someone from the list.`);
  }
  return parts.join(' ');
}

/**
 * A setting that lists people (Create my PPP: the leadership digest readers and the allow-list; owner, 2026-10-08: "SHOULD
 * BE ABLE TO CHOOSE USERS IN GYRO ADMIN"). It shows the people chosen, each with Remove, Remove all (confirmed by a
 * second button) once there are two, and a search box over the people Gyro knows (GET /v1/admin/people: desktop app users
 * and everyone with Gyro usage). Enter takes the highlighted person. Enter or Add take every full address typed or pasted,
 * so someone Gyro does not know yet can still be added, or else the only match. A typed address that begins exactly one
 * known address takes that person (several ask to pick), and one whose domain has no dot is refused as unfinished unless
 * Gyro knows it exactly. Words with an '@' that are not addresses, and a paste's entries with no address, stay in the box
 * with a note. The value stays one address per line, so the form's validation and patch are unchanged. Drawn from the
 * kit since the overhaul's step 4 (its classes are in kit.css).
 */
export function PeoplePicker({ id, label, value, max, onChange }: {
  /** The search box's id, for the field's label. */
  id: string;
  /** The field's plain name. */
  label: string;
  /** One address per line. */
  value: string;
  /** The most addresses the setting takes. */
  max: number;
  onChange: (value: string) => void;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<Search>({ status: 'idle' });
  const [active, setActive] = useState(-1);
  const [note, setNote] = useState<string | null>(null);
  // Remove all asks once more: the first click shows Cancel (focused, in its place) and "Yes, remove all N".
  const [clearing, setClearing] = useState(false);
  // Only the newest search's answer is shown: an older one that lands late is dropped.
  const asked = useRef(0);
  const box = useRef<HTMLInputElement>(null);
  const chips = useRef<HTMLUListElement>(null);
  const clearButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  // Where the focus goes once a removal has rendered: that person's Remove button, or the search box (null).
  const focusAfter = useRef<string | null | undefined>(undefined);
  // Whether Remove all was just armed (focus Cancel) or disarmed (focus Remove all) by the person, once it has rendered.
  const armFocus = useRef<'cancel' | 'clear' | null>(null);

  const chosen = peopleIn(value);
  const taken = new Set(chosen.map((email) => email.toLowerCase()));
  const query = text.trim();
  const typed = typedPeople(text);
  const pasted = isPaste(text, typed);
  const current = search.status === 'done' && search.query === query ? search : null;
  const known = new Set<string>(current?.people.map((person) => person.email));
  const matches = current ? current.people.filter((person) => !taken.has(person.email)) : [];
  const offered = matches.slice(0, SHOWN);
  // A search still being typed, not an address to add: one typed address that is not someone Gyro knows but begins the
  // address of people it does (`longer`), and any typed address with no dot after the '@' that it does not know exactly.
  const single = typed.people.length === 1 && typed.rejected.length === 0 && !known.has(typed.people[0]!) ? typed.people[0]! : null;
  const longer = single && current ? current.people.filter((person) => person.email.startsWith(single)) : [];
  const unfinished = typed.people.filter((email) => !looksFinished(email) && !known.has(email));
  // What Enter or Add would add as typed.
  const ready = longer.length > 0 ? [] : typed.people.filter((email) => !unfinished.includes(email));
  const listId = `${id}-people`;

  useEffect(() => {
    // A pasted list is not searched: Enter or Add takes every address in it.
    if (!open || pasted) return;
    const ticket = ++asked.current;
    const timer = setTimeout(() => {
      setSearch({ status: 'searching' });
      api.people(query.slice(0, PEOPLE_QUERY_MAX), PEOPLE_LIMIT).then(
        (found) => {
          if (ticket !== asked.current) return;
          setSearch({ status: 'done', query, people: found.people, total: found.total, complete: found.complete });
          setActive(-1);
        },
        (error: unknown) => {
          if (ticket === asked.current) setSearch({ status: 'failed', code: error instanceof ApiError ? error.status : null });
        },
      );
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, open, pasted]);

  // A removal took the focused Remove button with its chip: once it has rendered, the focus moves where remove() said.
  useEffect(() => {
    const next = focusAfter.current;
    if (next === undefined) return;
    focusAfter.current = undefined;
    const button = next === null
      ? undefined
      : Array.from(chips.current?.querySelectorAll('button') ?? []).find((element) => element.getAttribute('aria-label') === `Remove ${next}`);
    (button ?? box.current)?.focus();
  });

  /** Adds `emails`, leaves `rest` in the box and shows `restNote`, which says why the rest was not added. */
  const add = (emails: string[], rest = '', restNote: string | null = null) => {
    const fresh = emails.filter((email) => !taken.has(email.toLowerCase()));
    if (fresh.length === 0) {
      const already = emails.length === 1 ? `${emails[0]} is already in the list.` : 'They are already in the list.';
      setNote(restNote ? `${already} ${restNote}` : already);
      return;
    }
    if (chosen.length + fresh.length > max) {
      setNote(`${label}: at most ${max.toLocaleString('en-US')} ${max === 1 ? 'address' : 'addresses'}.`);
      return;
    }
    onChange(withPeople(value, fresh));
    setText(rest);
    setActive(-1);
    setClearing(false);
    setNote(restNote);
  };

  /**
   * Enter (`highlight`) takes the highlighted person. Otherwise Enter and Add take the addresses typed or pasted: a single
   * one that begins exactly one known address takes that person instead (several ask to pick), and unfinished addresses
   * and words that are not addresses stay in the box. With no address typed, they take the only match.
   */
  const take = (highlight: boolean) => {
    const highlighted = highlight && active >= 0 ? offered[active] : undefined;
    if (highlighted) return add([highlighted.email]);
    if (longer.length > 0) {
      const free = longer.filter((person) => !taken.has(person.email));
      if (free.length === 1) return add([free[0]!.email]);
      if (free.length > 1) return setNote('Pick from the list, or finish the address.');
      // Everyone it could be is chosen already: add() says so, and the half-typed address is not added.
      return add(longer.map((person) => person.email));
    }
    // A paste also keeps its entries with no address at all (a name on its own), so nothing in it vanishes unreported.
    const notAddresses = pasted ? [...typed.rejected, ...typed.unmatched] : typed.rejected;
    const left = [...notAddresses, ...unfinished];
    const leftNote = left.length > 0 ? leftText(notAddresses, unfinished) : null;
    // Separated by ';' as Outlook separates entries, so Enter again reads each one on its own.
    if (ready.length > 0) return add(ready, left.join('; '), leftNote);
    if (leftNote) return setNote(leftNote);
    if (query && offered.length === 1 && matches.length === 1) return add([offered[0]!.email]);
    setNote(query ? 'Pick someone from the list, or type their full e-mail address.' : null);
  };

  const remove = (email: string) => {
    const at = chosen.indexOf(email);
    focusAfter.current = chosen[at + 1] ?? chosen[at - 1] ?? null;
    onChange(withoutPerson(value, email));
    setClearing(false);
    setNote(null);
  };

  const removeAll = () => {
    focusAfter.current = null;
    onChange('');
    setClearing(false);
    setNote(null);
  };

  /** Shows (or hides) the confirmation for Remove all; the focus follows once it has rendered. */
  const arm = (on: boolean) => {
    armFocus.current = on ? 'cancel' : 'clear';
    setClearing(on);
  };

  useEffect(() => {
    const target = armFocus.current;
    armFocus.current = null;
    if (target === 'cancel') cancelButton.current?.focus();
    else if (target === 'clear') clearButton.current?.focus();
  }, [clearing]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(offered.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(-1, index - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      take(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  let foot: string | null = null;
  if (pasted) {
    foot = ready.length > 1 ? `Press Enter or Add to add these ${ready.length.toLocaleString('en-US')} addresses.`
      : ready.length === 1 ? 'Press Enter or Add to add this address.'
      : null;
  } else if (search.status === 'failed') foot = failedText(search.code);
  else if (!current) foot = search.status === 'idle' ? null : 'Searching…';
  else {
    // An answer cut at the limit can hide people not chosen yet, so the footer then never says no one or everyone.
    const cut = current.total > current.people.length;
    if (offered.length === 0) {
      foot = ready.length > 0 ? 'Press Enter or Add to add this address.'
        : cut ? 'More people match. Keep typing to narrow the list.'
        : current.people.length > 0 ? (query ? 'Everyone who matches is already in the list.' : 'Everyone Gyro knows is already in the list.')
        : query ? 'No one Gyro knows matches. Type their full e-mail address.'
        : 'Gyro knows no one yet. Type a full e-mail address.';
    } else if (matches.length > SHOWN || cut) {
      foot = 'More people match. Keep typing to narrow the list.';
    }
    if (!current.complete) foot = `${foot ? `${foot} ` : ''}Some of the people Gyro knows could not be read just now, so someone may be missing.`;
  }

  return (
    <div className="people-picker">
      {chosen.length > 0 ? (
        <ul ref={chips} className="people-chips" aria-label={`${label}: ${chosen.length} chosen`}>
          {chosen.map((email) => (
            <li key={email} className={isAddress(email) ? 'people-chip' : 'people-chip bad'} title={isAddress(email) ? undefined : 'Not an e-mail address. Remove it before saving.'}>
              <span>{email}</span>
              <button type="button" aria-label={`Remove ${email}`} onClick={() => remove(email)}><X size={12} aria-hidden="true" /></button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="ui-cell-sub">No one yet.</span>
      )}
      {chosen.length >= 2 ? (
        <div className="ui-actions people-clear">
          {clearing ? (
            <>
              {/* Cancel takes Remove all's place and the focus, so a double-click or a held key cancels instead of confirming. */}
              <Button ref={cancelButton} size="sm" aria-label={`Cancel removing everyone from ${label}`} onClick={() => arm(false)}>Cancel</Button>
              <Button variant="danger" size="sm" aria-label={`Yes, remove all ${chosen.length.toLocaleString('en-US')} from ${label}`} onClick={removeAll}>
                Yes, remove all {chosen.length.toLocaleString('en-US')}
              </Button>
            </>
          ) : (
            <Button ref={clearButton} variant="ghost" size="sm" aria-label={`Remove all from ${label}`} onClick={() => arm(true)}>Remove all</Button>
          )}
        </div>
      ) : null}
      <div className="people-search">
        <div className="ui-actions">
          <Input
            ref={box}
            id={id}
            className="ui-grow"
            role="combobox"
            aria-label={label}
            aria-autocomplete="list"
            aria-expanded={open && offered.length > 0}
            aria-controls={listId}
            aria-activedescendant={active >= 0 && offered[active] ? `${listId}-${active}` : undefined}
            placeholder="Search by e-mail, or type a full address"
            autoComplete="off"
            spellCheck={false}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setOpen(true);
              setNote(null);
            }}
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              setOpen(false);
              setActive(-1);
            }}
          />
          <Button icon={Plus} aria-label={`Add to ${label}`} disabled={!query} onClick={() => take(false)}>Add</Button>
        </div>
        {open && offered.length > 0 ? (
          <ul id={listId} role="listbox" className="people-suggest" aria-label="People Gyro knows">
            {offered.map((person, index) => (
              <li
                key={person.email}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => add([person.email])}
              >
                {person.email}
              </li>
            ))}
          </ul>
        ) : null}
        {/* Always rendered, so a screen reader announces each change of its text. */}
        <div className="ui-cell-sub people-foot" role="status">{open ? foot : null}</div>
      </div>
      {note ? <div className="ui-note is-bad people-note" role="alert"><span>{note}</span></div> : null}
    </div>
  );
}
